import React from 'react';

import RadioCard from 'components/RadioCard';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { GroupedListModel } from 'models/groupedList';

import ApprovalLevel from './components/ApprovalLevel';
import { Container, ErrorMessage } from './styles';

interface Props {
  setFieldValue: any;
  values: any;
  errors: any;
  submitted: boolean;
  onConfirmAuthorizationApproval: (value: string, formikFieldName: string) => void;
  onConfirmAuthorizationUsers: (value: GroupedListModel[], formikFieldName: string) => void;
}

interface LevelsProps {
  setFieldValue: any;
  values: any;
  errors: any;
  submitted: boolean;
  secondary?: boolean;
  onConfirmAuthorizationApproval: (value: string, formikFieldName: string) => void;
  onConfirmAuthorizationUsers: (value: GroupedListModel[], formikFieldName: string) => void;
}

const Levels = ({
  setFieldValue,
  values,
  errors,
  submitted,
  secondary,
  onConfirmAuthorizationApproval,
  onConfirmAuthorizationUsers,
}: LevelsProps) => {
  const { t } = useStoreTranslation();

  const authorizationPrimaryUsers = {
    value: values.authorizationPrimaryUsers,
    error: submitted && errors?.authorizationPrimaryUsers?.authorizationUsers,
  };
  const authorizationSecondaryUsers = {
    value: values.authorizationSecondaryUsers,
    error: submitted && errors?.authorizationSecondaryUsers?.authorizationUsers,
  };

  return (
    <>
      <ApprovalLevel
        title={t('group.approvalMethod.primaryApproval')}
        formikValues={values}
        setFieldValue={setFieldValue}
        formikFieldName="authorizationPrimaryUsers"
        userExclusion={authorizationPrimaryUsers.value.authorizationUsers}
        onConfirmAuthorizationApproval={onConfirmAuthorizationApproval}
        onConfirmAuthorizationUsers={onConfirmAuthorizationUsers}
        errorMessage={authorizationPrimaryUsers.error}
      />
      {secondary && (
        <ApprovalLevel
          title={t('group.approvalMethod.secondaryApproval')}
          formikValues={values}
          setFieldValue={setFieldValue}
          formikFieldName="authorizationSecondaryUsers"
          userExclusion={authorizationSecondaryUsers.value.authorizationUsers}
          onConfirmAuthorizationApproval={onConfirmAuthorizationApproval}
          onConfirmAuthorizationUsers={onConfirmAuthorizationUsers}
          errorMessage={authorizationSecondaryUsers.error}
        />
      )}
    </>
  );
};

const Authorization = ({
  setFieldValue,
  values,
  errors,
  submitted,
  onConfirmAuthorizationApproval,
  onConfirmAuthorizationUsers,
}: Props) => {
  const { t } = useStoreTranslation();

  const authorizationType = { value: values.authorizationType, error: submitted && errors?.authorizationType };
  const authorizationPrimaryUsers = {
    value: values.authorizationPrimaryUsers,
    error: submitted && errors?.authorizationPrimaryUsers?.authorizationUsers,
  };
  const authorizationSecondaryUsers = {
    value: values.authorizationSecondaryUsers,
    error: submitted && errors?.authorizationSecondaryUsers?.authorizationUsers,
  };

  const handleChange = (value: string) => {
    setFieldValue('authorizationType', value);

    if (value.toLowerCase() === 'simple') {
      authorizationSecondaryUsers.value.authorizationUsers = [];
    } else if (value.toLowerCase() === 'never') {
      authorizationSecondaryUsers.value.authorizationUsers = [];
      authorizationPrimaryUsers.value.authorizationUsers = [];
    }
  };

  return (
    <Container>
      <RadioCard
        title={t('group.approvalMethod.methods.simple')}
        subtitle={t('group.approvalMethod.methods.simpleDescription')}
        optionValue={authorizationType.value}
        value="simple"
        handleChangeValue={handleChange}
        errorMessage={authorizationType.error}
      >
        <Levels
          setFieldValue={setFieldValue}
          values={values}
          errors={errors}
          submitted={submitted}
          onConfirmAuthorizationApproval={onConfirmAuthorizationApproval}
          onConfirmAuthorizationUsers={onConfirmAuthorizationUsers}
        />
      </RadioCard>
      <RadioCard
        title={t('group.approvalMethod.methods.sequential')}
        subtitle={t('group.approvalMethod.methods.sequentialDescription')}
        optionValue={authorizationType.value}
        value="sequential"
        handleChangeValue={handleChange}
        errorMessage={authorizationType.error}
      >
        <Levels
          secondary
          setFieldValue={setFieldValue}
          values={values}
          errors={errors}
          submitted={submitted}
          onConfirmAuthorizationApproval={onConfirmAuthorizationApproval}
          onConfirmAuthorizationUsers={onConfirmAuthorizationUsers}
        />
      </RadioCard>
      <RadioCard
        title={t('group.approvalMethod.methods.parallel')}
        subtitle={t('group.approvalMethod.methods.parallelDescription')}
        optionValue={authorizationType.value}
        value="parallel"
        handleChangeValue={handleChange}
        errorMessage={authorizationType.error}
      >
        <Levels
          secondary
          setFieldValue={setFieldValue}
          values={values}
          errors={errors}
          submitted={submitted}
          onConfirmAuthorizationApproval={onConfirmAuthorizationApproval}
          onConfirmAuthorizationUsers={onConfirmAuthorizationUsers}
        />
      </RadioCard>
      {authorizationType.error && <ErrorMessage>{t(authorizationType.error)}</ErrorMessage>}
    </Container>
  );
};

export default Authorization;
